/**
 * Product Management System - Moon Moon Agency v2.1
 * Shopify product page with variant selection, media updates, and AJAX
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const PRODUCT_CONFIG = {
	// API Settings
	debounceDelay: 300,
	apiTimeout: 10000,
	
	
	// Selectors
	selectors: {
		variantSelector: 'variant-selector',
		productForm: '#product-form',
		variantInput: '[data-selected-variant-id]',
		productReload: '[data-product-reload]',
		variantFormValue: '[data-variant-value]',
		variantCheckbox: '[data-variant-input]:checked'
	},

};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */

// Product Event Emitter
const productEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'product'
	};
	
	return document.dispatchEvent(new CustomEvent(`product:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache for global elements
const ProductDOM = {
	get stickyCTA() { return document.querySelector('[data-sticky-cta]'); },
	get classicCTA() { return document.querySelector('[data-cta]'); },
	get priceElements() { return document.querySelectorAll('[data-price]'); }
};

// Product Data Management
const ProductManager = {
	updateVariantId(container, variantId) {
		if (!variantId) return;
		
		const variantInput = container?.querySelector(PRODUCT_CONFIG.selectors.variantInput);
		if (variantInput && variantInput.value !== variantId.toString()) {
			const oldValue = variantInput.value;
			variantInput.value = variantId;
			window.log('info', 'Product', 'Variant ID updated', { 
				oldVariantId: oldValue, 
				newVariantId: variantId,
				inputElement: variantInput.id || 'no-id',
				containerElement: container?.id || 'no-id'
			});
			productEmit('variant-input-updated', { variantId });
		}
	},

	updateReloadElements(html) {
		const elementsToUpdate = document.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);
		const newElements = html.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);
		
		productEmit('product-reload-start', { elementsCount: elementsToUpdate.length });

		let updatedCount = 0;
		
		elementsToUpdate.forEach((currentElement, index) => {
			// Try to match by ID first, then by specific class, fallback to index
			let newElement = null;
			
			if (currentElement.id) {
				newElement = html.getElementById(currentElement.id);
			} else if (currentElement.className) {
				// Match by specific component classes for better targeting
				const classList = Array.from(currentElement.classList);
				const specificClass = classList.find(cls => 
					cls.includes('component-price') || 
					cls.includes('component-quantity') || 
					cls.includes('component-cta') ||
					cls.includes('sticky-cta')
				);
				
				if (specificClass) {
					const candidates = html.querySelectorAll(`.${specificClass}[data-product-reload]`);
					newElement = candidates[0];
				}
			}
			
			// Fallback to index-based matching
			if (!newElement) {
				newElement = newElements[index];
			}
			
			if (newElement && this.shouldUpdateElement(currentElement, newElement)) {
				try {
					currentElement.outerHTML = newElement.outerHTML;
					updatedCount++;
					productEmit('product-reload-element-updated', { 
						elementIndex: index,
						matchType: currentElement.id ? 'id' : (currentElement.className.includes('component') ? 'class' : 'index')
					});
				} catch (error) {
					window.log('warn', 'Product', 'Element update failed', { 
						index, 
						elementId: currentElement.id || 'no-id',
						elementTag: currentElement.tagName,
						elementClasses: currentElement.className,
						error: error.message 
					});
				}
			}
		});

		window.log('info', 'Product', 'DOM reload completed', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount,
			skippedElements: elementsToUpdate.length - updatedCount,
			elementTypes: [...new Set(Array.from(elementsToUpdate).map(el => el.tagName.toLowerCase()))]
		});
		
		productEmit('product-reload-complete', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount
		});
	},

	shouldUpdateElement(currentElement, newElement) {
		const currentContent = currentElement.innerHTML?.trim();
		const newContent = newElement.innerHTML?.trim();
		return currentContent !== newContent;
	}
};


/*********************************************************************************************
 * 🔄 VARIANT SELECTION LOGIC
 */
class VariantSelector extends HTMLElement {
	constructor() {
		super();
		this.cached = new Map();
		this.currentVariant = null;
		this.options = [];
		this.variantData = null;
		
		this.container = this.closest(PRODUCT_CONFIG.selectors.productForm);
		if (!this.container) {
			window.log('error', 'Product', 'VariantSelector: No product form container found');
			return;
		}
		
		this.onVariantChange = this.onVariantChange.bind(this);
		this.init();
	}

	init() {
		productEmit('variant-selector-init-start');
		
		try {
			this.addEventListener("change", this.onVariantChange);
			this.getSelectedOptions();

			this.getSelectedVariant();
			
			productEmit('variant-selector-init-success');
		} catch (error) {
			window.log('error', 'Product', 'Failed to initialize VariantSelector', { error: error.message });
		}
	}

	onVariantChange() {
		productEmit('variant-change-start');

		try {
			setTimeout(() => {
				this.getSelectedOptions();
				this.getSelectedVariant();

				if (this.currentVariant) {
					productEmit('variant-found', {
						variantId: this.currentVariant.id,
						available: this.currentVariant.available
					});

					// Orchestrate all updates
					this.updateURL();
					this.updateProductData();
					this.updateMediaDisplay();
				} else {
					window.log('warn', 'Product', 'No matching variant found for options', { options: this.options });
					productEmit('variant-not-found', { options: this.options });
				}
			}, 10);
		} catch (error) {
			window.log('error', 'Product', 'Error in variant change', { error: error.message });
		}
	}

	/**
	 * Update product data
	 */
	async updateProductData() {
		// Update variant ID immediately
		ProductManager.updateVariantId(this.container, this.currentVariant.id);
		
		// Fetch and update product data
		const html = await this.fetchVariantData();
		if (html) {
			ProductManager.updateReloadElements(html);
			this.updateVariantOptions(html);
		}
	}

	/**
	 * Update media display
	 */
	updateMediaDisplay() {
		// Use MediaManager from the separate media module
		if (window.MediaManager?.updateImagesVariant) {
			window.MediaManager.updateImagesVariant(this.currentVariant);
		} else {
			window.log('warn', 'Product', 'MediaManager or updateImagesVariant method not available', {
				mediaManagerExists: !!window.MediaManager,
				hasUpdateMethod: !!window.MediaManager?.updateImagesVariant
			});
		}
	}

	/**
	 * Fetch variant data
	 */
	async fetchVariantData() {
		if (!this.currentVariant?.id) return null;
		
		const baseUrl = this.dataset.url || window.location.pathname;
		const sectionId = this.dataset.section;
		
		if (!sectionId) {
			window.log('warn', 'Product', 'VariantSelector: No section ID provided');
			return null;
		}

		const variantUrl = `${baseUrl}?variant=${this.currentVariant.id}&section_id=${sectionId}`;

		// Check cache first
		if (this.cached.has(variantUrl)) {
			return this.cached.get(variantUrl);
		}

		try {
			const response = await fetch(variantUrl);
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			
			const responseText = await response.text();
			const html = new DOMParser().parseFromString(responseText, 'text/html');
			
			// Cache the result
			this.cached.set(variantUrl, html);
			
			productEmit('variant-fetch-success', { variantId: this.currentVariant.id });
			return html;
		} catch (error) {
			window.log('error', 'Product', 'Failed to fetch variant data', { error: error.message });
			return null;
		}
	}

	/**
	 * Get selected options from variant inputs
	 */
	getSelectedOptions() {
		const inputs = this.querySelectorAll('[data-variant-input]');
		this.options = Array.from(inputs, input => input.value).filter(value => value);
	}

	/**
	 * Get the selected variant based on options
	 */
	getSelectedVariant() {
		const variants = this.getVariantJSON();
		
		if (!variants.length) {
			this.currentVariant = null;
			return;
		}

		this.currentVariant = variants.find(variant => {
			if (!variant.options || !Array.isArray(variant.options)) return false;
			return variant.options.every((option, index) => this.options[index] === option);
		});
	}

	/**
	 * Get variant data from embedded JSON
	 */
	getVariantJSON() {
		if (!this.variantData) {
			const jsonElement = this.querySelector('[type="application/json"]');
			
			if (!jsonElement) {
				window.log('error', 'Product', 'VariantSelector: No JSON data element found');
				return this.variantData = [];
			}
			
			try {
				const jsonText = jsonElement.textContent.trim();
				this.variantData = jsonText ? JSON.parse(jsonText) : [];
			} catch (error) {
				window.log('error', 'Product', 'Failed to parse variant JSON', { error: error.message });
				this.variantData = [];
			}
		}
		return this.variantData;
	}

	/**
	 * Update URL with selected variant
	 */
	updateURL() {
		if (!this.currentVariant?.id) return;
		
		try {
			const baseUrl = this.dataset.url || window.location.pathname;
			const url = new URL(baseUrl, window.location.origin);
			url.searchParams.set('variant', this.currentVariant.id);
			window.history.replaceState({}, '', url.toString());
		} catch (error) {
			window.log('error', 'Product', 'Failed to update URL', { error: error.message });
		}
	}

	/**
	 * Update variant options without breaking event listeners
	 */
	updateVariantOptions(html) {
		const currentSelects = this.querySelectorAll('[data-variant-input]');
		const newSelects = html.querySelectorAll('[data-variant-input]');

		if (!newSelects.length) return;

		currentSelects.forEach((currentSelect, index) => {
			const newSelect = newSelects[index];
			if (!newSelect) return;

			const currentValue = currentSelect.value;
			Array.from(newSelect.options).forEach((newOpt, i) => {
				const currentOpt = currentSelect.options[i];
				if (currentOpt) currentOpt.disabled = newOpt.disabled;
			});
			currentSelect.value = currentValue;
		});
	}
}

/*********************************************************************************************
 * 🏷️ UTILITY FUNCTIONS
 */

/**
 * Change quantity value and update prices
 */
function mmChangeQtty(delta, inputId) {
	const input = document.getElementById(inputId);
	if (!input) return;

	const currentValue = parseInt(input.value, 10) || 1;
	const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;
	const minValue = parseInt(input.getAttribute('min'), 10) || 1;

	// Calculate new value with constraints
	const newValue = Math.max(minValue, Math.min(maxValue, currentValue + parseInt(delta, 10)));

	input.value = newValue;
	input.dispatchEvent(new Event('change', { bubbles: true }));

	// Update prices
	mmUpdatePrice(newValue);

	// Update button states
	updateQuantityButtonStates(input);
}

/**
 * Update plus button disabled state based on current quantity
 */
function updateQuantityButtonStates(input) {
	const currentValue = parseInt(input.value, 10) || 1;
	const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

	// Find the container
	const container = input.closest('.mm-qtty-container');
	if (!container) return;

	// Find plus button
	const plusBtn = container.querySelector('[data-qtty-increase]');

	// Update plus button only
	if (plusBtn) {
		if (currentValue >= maxValue) {
			plusBtn.setAttribute('disabled', '');
		} else {
			plusBtn.removeAttribute('disabled');
		}
	}
}

/**
 * Update displayed prices based on quantity
 */
function mmUpdatePrice(qtty) {
	const quantity = parseInt(qtty, 10);
	if (!quantity || quantity < 1) return;

	productEmit('quantity-update-start', { quantity });

	try {
		let updatedElementsCount = 0;

		// Update price elements
		ProductDOM.priceElements.forEach(priceElement => {
			const priceValue = parseFloat(priceElement.dataset.price);
			if (!isNaN(priceValue)) {
				const totalPrice = (priceValue * quantity) / 100;
				const currency = window.Shopify?.currency?.active || 'EUR';
				const locale = document.documentElement.lang || 'en';
				const formattedPrice = new Intl.NumberFormat(locale, {
					style: 'currency',
					currency: currency
				}).format(totalPrice);
				priceElement.textContent = formattedPrice;
				updatedElementsCount++;
			}
		});

		productEmit('quantity-update-success', { 
			quantity, 
			updatedElements: updatedElementsCount 
		});
		
		window.log('info', 'Product', 'Price update completed', {
			quantity,
			updatedPriceElements: updatedElementsCount
		});
	} catch (error) {
		window.log('error', 'Product', 'Failed to update prices', { 
			quantity, 
			error: error.message 
		});
		productEmit('quantity-update-error', { 
			quantity, 
			error: error.message 
		});
	}
}

/*********************************************************************************************
 * 📌 STICKY CTA & REASSURANCE
 */

/**
 * Initialize sticky CTA behavior
 */
function initializeStickyCTA() {
	const classicCTA = ProductDOM.classicCTA;
	const stickyCTA = ProductDOM.stickyCTA;

	if (!classicCTA || !stickyCTA) {
		window.log('warn', 'Product', 'StickyCTA: Required elements not found');
		return;
	}

	// Disconnect previous observer if element was replaced by product-reload
	if (classicCTA._stickyObserver) {
		classicCTA._stickyObserver.disconnect();
	}

	try {
		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				const isVisible = entry.isIntersecting;
				stickyCTA.classList.toggle('mm-none', isVisible);
			});
		}, {
			threshold: 0,
			rootMargin: '0px 0px -10px 0px'
		});

		observer.observe(classicCTA);
		classicCTA._stickyObserver = observer;

	} catch (error) {
		window.log('error', 'Product', 'Failed to initialize sticky CTA', { error: error.message });
	}
}

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */

// Define custom element
customElements.define(PRODUCT_CONFIG.selectors.variantSelector, VariantSelector);

/*********************************************************************************************
 * 🚀 INITIALIZATION & ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */
document.addEventListener('DOMContentLoaded', () => {
	productEmit('initializing');

	try {
		initializeStickyCTA();
		SizeGuideManager.init();
		productEmit('initialized');
		window.log('info', 'Product', 'Product page initialized successfully');
	} catch (error) {
		window.log('error', 'Product', 'Failed to initialize product page', { error: error.message });
	}

	// Initialize button states on page load
	const inputs = document.querySelectorAll('input[data-qtty-input]');
	inputs.forEach((input) => {
		updateQuantityButtonStates(input);
	});

	// Use event delegation for quantity buttons (works even after DOM updates)
	// Product page buttons use data-qtty-decrease/increase
	// Cart buttons use data-cart-decrease/increase (handled by mm_cart.js)
	document.addEventListener('click', (e) => {
		// Handle decrease buttons
		const decreaseBtn = e.target.closest('[data-qtty-decrease]');
		if (decreaseBtn) {
			const inputId = decreaseBtn.dataset.qttyDecrease;
			window.mmChangeQtty(-1, inputId);
			return;
		}

		// Handle increase buttons
		const increaseBtn = e.target.closest('[data-qtty-increase]');
		if (increaseBtn) {
			const inputId = increaseBtn.dataset.qttyIncrease;
			window.mmChangeQtty(1, inputId);
			return;
		}
	});

	// Re-attach sticky CTA observer after DOM nodes are replaced by product-reload
	document.addEventListener('product:product-reload-complete', () => {
		initializeStickyCTA();
	});

	// Adjust quantity when product reload completes
	document.addEventListener('product:product-reload-complete', () => {
		const inputs = document.querySelectorAll('input[data-qtty-input]');
		inputs.forEach((input) => {
			const currentValue = parseInt(input.value, 10) || 1;
			const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

			if (currentValue > maxValue) {
				input.value = maxValue;
			}

			updateQuantityButtonStates(input);
		});

		if (inputs.length > 0) {
			const newValue = parseInt(inputs[0].value, 10) || 1;
			mmUpdatePrice(newValue);
		}
	});
});

/*********************************************************************************************
 * 📐 SIZE GUIDE PANEL
 */
const SizeGuideManager = {
	config: {
		selectors: {
			panel: '[data-size-guide-panel]',
			overlay: '[data-size-guide-overlay]',
			openTrigger: '[data-open-size-guide]',
			closeBtn: '[data-close-size-guide]',
		},
		classes: {
			open: 'mm-size-guide-panel--open',
			overlayOpen: 'mm-size-guide-overlay--open',
		},
	},

	init() {
		const panel = window.getElement(this.config.selectors.panel);
		if (!panel) return;

		this._bindEvents();
		window.log('info', '📐 [SIZE_GUIDE]', 'Initialized');
	},

	_bindEvents() {
		document.addEventListener('click', (e) => {
			if (e.target.closest(this.config.selectors.openTrigger)) {
				this.open();
				return;
			}
			if (e.target.closest(this.config.selectors.closeBtn)) {
				this.close();
				return;
			}
			if (e.target.matches(this.config.selectors.overlay)) {
				this.close();
			}
		});

		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') this.close();
		});

		document.addEventListener('cart:api-success', () => {
			const panel = window.getElement(this.config.selectors.panel);
			if (panel?.getAttribute('aria-hidden') === 'false') this.close();
		});
	},

	open() {
		const panel = window.getElement(this.config.selectors.panel);
		const overlay = window.getElement(this.config.selectors.overlay);
		if (!panel) return;

		panel.classList.add(this.config.classes.open);
		overlay?.classList.add(this.config.classes.overlayOpen);
		panel.setAttribute('aria-hidden', 'false');
		overlay?.setAttribute('aria-hidden', 'false');
		document.documentElement.classList.add('stop-scrolling');
		this._lastFocus = document.activeElement;

		const closeBtn = panel.querySelector(this.config.selectors.closeBtn);
		closeBtn?.focus();
		window.log('info', '📐 [SIZE_GUIDE]', 'Opened');
	},

	close() {
		const panel = window.getElement(this.config.selectors.panel);
		const overlay = window.getElement(this.config.selectors.overlay);
		if (!panel) return;

		panel.classList.remove(this.config.classes.open);
		overlay?.classList.remove(this.config.classes.overlayOpen);
		panel.setAttribute('aria-hidden', 'true');
		overlay?.setAttribute('aria-hidden', 'true');
		document.documentElement.classList.remove('stop-scrolling');
		this._lastFocus?.focus();
		window.log('info', '📐 [SIZE_GUIDE]', 'Closed');
	},
};

/*********************************************************************************************
 * 🔧 PRODUCT TABS MANAGER
 */
const ProductTabsManager = {
	config: {
		selectors: {
			nav: '.mm-product-tabs__nav',
			trigger: '.mm-product-tabs__trigger',
			panel: '.mm-product-tabs__panel',
			accordion: '.mm-product-tabs__accordion',
		},
		classes: {
			triggerActive: 'mm-product-tabs__trigger--active',
			panelActive: 'mm-product-tabs__panel--active',
		},
	},

	init() {
		const nav = window.getElement(this.config.selectors.nav);
		if (!nav) return;

		if (!window.isMobile()) {
			document.querySelectorAll(this.config.selectors.accordion).forEach((d) => d.setAttribute('open', ''));
		}

		this._bindEvents();
		window.log('info', '🗂️ [PRODUCT_TABS]', 'Initialized');
	},

	_bindEvents() {
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest(this.config.selectors.trigger);
			if (!trigger) return;
			this._activateTab(trigger);
		});

		document.addEventListener('keydown', (e) => {
			const trigger = e.target.closest(this.config.selectors.trigger);
			if (!trigger) return;

			const triggers = [...document.querySelectorAll(this.config.selectors.trigger)];
			const index = triggers.indexOf(trigger);

			if (e.key === 'ArrowRight') {
				e.preventDefault();
				const next = triggers[(index + 1) % triggers.length];
				next.focus();
				this._activateTab(next);
			} else if (e.key === 'ArrowLeft') {
				e.preventDefault();
				const prev = triggers[(index - 1 + triggers.length) % triggers.length];
				prev.focus();
				this._activateTab(prev);
			}
		});
	},

	_activateTab(trigger) {
		const tabId = trigger.dataset.tab;
		const panelId = trigger.getAttribute('aria-controls');

		if (window.isMobile()) {
			// Mobile : toggle open sur le <details> correspondant
			const accordions = [...document.querySelectorAll(this.config.selectors.accordion)];
			accordions.forEach((details) => {
				const panel = details.querySelector(this.config.selectors.panel);
				const matchesTab = panel && panel.id === panelId;
				if (matchesTab) {
					details.setAttribute('open', '');
				} else {
					details.removeAttribute('open');
				}
			});
		} else {
			// Desktop : toggle --active sur les panels
			const triggers = [...document.querySelectorAll(this.config.selectors.trigger)];
			const panels = [...document.querySelectorAll(this.config.selectors.panel)];

			triggers.forEach((t) => {
				t.classList.remove(this.config.classes.triggerActive);
				t.setAttribute('aria-selected', 'false');
				t.setAttribute('tabindex', '-1');
			});

			panels.forEach((p) => p.classList.remove(this.config.classes.panelActive));

			trigger.classList.add(this.config.classes.triggerActive);
			trigger.setAttribute('aria-selected', 'true');
			trigger.removeAttribute('tabindex');

			const panel = document.getElementById(panelId);
			if (panel) panel.classList.add(this.config.classes.panelActive);
		}

		window.log('info', '🗂️ [PRODUCT_TABS]', `Tab activated: ${tabId}`);
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => ProductTabsManager.init());
} else {
	ProductTabsManager.init();
}

/*********************************************************************************************
 * 🏪 STORE PANEL MANAGER
 */
const StorePanelManager = {
	config: {
		selectors: {
			panel: '[data-store-panel]',
			overlay: '[data-store-panel-overlay]',
			openTrigger: '[data-open-store-panel]',
			closeBtn: '[data-close-store-panel]',
			filter: '[data-store-panel-filter]',
			filterOption: '[data-panel-option]',
			list: '#mm-store-panel-list',
			listAll: '#mm-store-panel-list-all',
			hiddenSelect: '.mm-variant-select-hidden',
		},
		classes: {
			open: 'mm-store-panel--open',
			overlayOpen: 'mm-store-panel-overlay--open',
			filterActive: 'mm-store-panel__filter-option--active',
		},
	},

	_currentVariantId: null,

	init() {
		const panel = window.getElement(this.config.selectors.panel);
		if (!panel) return;

		this._bindEvents();
		window.log('info', '🏪 [STORE_PANEL]', 'Initialized');
	},

	_bindEvents() {
		document.addEventListener('click', (e) => {
			if (e.target.closest(this.config.selectors.openTrigger)) {
				this.open();
				return;
			}
			if (e.target.closest(this.config.selectors.closeBtn)) {
				this.close();
				return;
			}
			if (e.target.matches(this.config.selectors.overlay)) {
				this.close();
				return;
			}
			const filterBtn = e.target.closest(this.config.selectors.filterOption);
			if (filterBtn) {
				this._onFilterClick(filterBtn);
			}
		});

		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') this.close();
		});

		document.addEventListener('product:variant-found', (e) => {
			const variantId = e.detail?.data?.variantId;
			window.log('info', '🏪 [STORE_PANEL]', 'variant-found received', { variantId, currentVariantId: this._currentVariantId });
			if (!variantId || variantId === this._currentVariantId) return;
			this._currentVariantId = variantId;
			this._syncFilterToVariant(variantId);
			this._refreshList(variantId);
		});
	},

	_onFilterClick(btn) {
		const option = btn.dataset.panelOption;
		this._setActiveFilter(btn);

		if (option === 'all') {
			this._showAllList();
			return;
		}

		const variantsData = document.querySelector('variant-selector script[type="application/json"]');
		if (!variantsData) return;

		try {
			const variants = JSON.parse(variantsData.textContent);
			const match = variants.find(v => v.options && v.options.includes(option));
			if (!match) return;

			// Update hidden selects (source of truth for VariantSelector)
			const hiddenSelects = document.querySelectorAll('variant-selector ' + this.config.selectors.hiddenSelect);
			hiddenSelects.forEach((select, i) => {
				const newValue = match.options[i];
				if (newValue !== undefined && select.value !== String(newValue)) {
					select.value = newValue;
					select.dispatchEvent(new Event('change', { bubbles: true }));
				}
			});

			// Update visible dropdown label — scoped to variant-selector only
			document.querySelectorAll('variant-selector [data-product-dropdown]').forEach((dropdown, i) => {
				const newValue = match.options[i];
				if (newValue !== undefined) window.ProductDropdownManager?.syncValue(dropdown, newValue);
			});
		} catch (e) {
			window.log('warn', '🏪 [STORE_PANEL]', 'Could not parse variants JSON');
		}
	},

	_setActiveFilter(activeBtn) {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) return;

		filter.querySelectorAll(this.config.selectors.filterOption).forEach(btn => {
			btn.classList.remove(this.config.classes.filterActive);
			btn.setAttribute('aria-pressed', 'false');
		});
		activeBtn.classList.add(this.config.classes.filterActive);
		activeBtn.setAttribute('aria-pressed', 'true');
	},

	_showAllList() {
		const list = window.getElement(this.config.selectors.list);
		const listAll = window.getElement(this.config.selectors.listAll);
		if (list) list.hidden = true;
		if (listAll) listAll.hidden = false;
	},

	_showVariantList() {
		const list = window.getElement(this.config.selectors.list);
		const listAll = window.getElement(this.config.selectors.listAll);
		if (list) list.hidden = false;
		if (listAll) listAll.hidden = true;
	},

	_syncFilterToVariant(variantId) {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) return;

		const variantsData = document.querySelector('variant-selector script[type="application/json"]');
		if (!variantsData) return;

		try {
			const variants = JSON.parse(variantsData.textContent);
			const variant = variants.find(v => String(v.id) === String(variantId));
			if (!variant) return;

			const option = variant.options?.[0];
			const btn = filter.querySelector(`[data-panel-option="${option}"]`);
			if (btn) this._setActiveFilter(btn);
		} catch (e) {
			window.log('warn', '🏪 [STORE_PANEL]', 'Could not sync filter to variant');
		}
	},

	_refreshList(variantId) {
		this._showVariantList();
		const list = window.getElement(this.config.selectors.list);
		if (!list) return;

		const variantSelector = window.getElement('variant-selector');
		const sectionId = variantSelector?.dataset?.section;
		if (!sectionId) {
			window.log('warn', '🏪 [STORE_PANEL]', 'No section_id found for list refresh');
			return;
		}

		const baseUrl = variantSelector.dataset.url || window.location.pathname;
		const url = `${baseUrl}?variant=${variantId}&section_id=${sectionId}`;

		window.log('info', '🏪 [STORE_PANEL]', 'Fetching list', { url });

		fetch(url)
			.then(r => {
				window.log('info', '🏪 [STORE_PANEL]', 'Fetch response', { status: r.status, ok: r.ok });
				return r.text();
			})
			.then(html => {
				const doc = new DOMParser().parseFromString(html, 'text/html');
				const newList = doc.getElementById('mm-store-panel-list');
				window.log('info', '🏪 [STORE_PANEL]', 'New list found in response', { found: !!newList, innerHTML: newList?.innerHTML?.slice(0, 100) });
				if (newList) list.innerHTML = newList.innerHTML;
			})
			.catch(err => window.log('warn', '🏪 [STORE_PANEL]', 'List refresh failed', { error: err.message }));
	},

	open() {
		const panel = window.getElement(this.config.selectors.panel);
		const overlay = window.getElement(this.config.selectors.overlay);
		if (!panel) return;

		panel.classList.add(this.config.classes.open);
		overlay?.classList.add(this.config.classes.overlayOpen);
		panel.setAttribute('aria-hidden', 'false');
		overlay?.setAttribute('aria-hidden', 'false');
		document.documentElement.classList.add('stop-scrolling');
		this._lastFocus = document.activeElement;

		const selectedVariantInput = window.getElement('[data-selected-variant-id]');
		if (selectedVariantInput) {
			const variantId = parseInt(selectedVariantInput.value, 10);
			if (variantId && variantId !== this._currentVariantId) {
				this._currentVariantId = variantId;
				this._syncFilterToVariant(variantId);
			}
		}

		panel.querySelector(this.config.selectors.closeBtn)?.focus();
		window.log('info', '🏪 [STORE_PANEL]', 'Opened');
	},

	close() {
		const panel = window.getElement(this.config.selectors.panel);
		const overlay = window.getElement(this.config.selectors.overlay);
		if (!panel) return;

		panel.classList.remove(this.config.classes.open);
		overlay?.classList.remove(this.config.classes.overlayOpen);
		panel.setAttribute('aria-hidden', 'true');
		overlay?.setAttribute('aria-hidden', 'true');
		document.documentElement.classList.remove('stop-scrolling');
		this._lastFocus?.focus();
		window.log('info', '🏪 [STORE_PANEL]', 'Closed');
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => StorePanelManager.init());
} else {
	StorePanelManager.init();
}

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (window.isDev) {
	// Product events
	['initializing', 'initialized', 'variant-change-start', 'variant-found', 'variant-not-found',
		'quantity-update-start', 'quantity-update-success', 'quantity-update-error'].forEach(event => {
		document.addEventListener(`product:${event}`, e =>
			console.log(`🛍️ [PRODUCT] ${event}:`, e.detail)
		);
	});

}